import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core'
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from '@angular/material/core'
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
  MatDatepickerModule,
} from '@angular/material/datepicker'
import { UntilDestroy } from '@ngneat/until-destroy'
import { DateFilter } from '@wm-front/admin/graphql'
import { I18nService } from '@wm-front/core'
import dayJS from 'dayjs'
import { FormsModule } from '@angular/forms'
import {
  DayjsDateAdapter,
  MAT_DAYJS_DATE_ADAPTER_OPTIONS,
} from '../../helpers/date-adapter.class'

import { InputDateComponent } from '../input-date/input-date.component'
import { DateRangePickerHeaderComponent } from '../date-range-picker-header/date-range-picker-header.component'
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TrackChanges } from '@wm-front/shared/utils'
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2'
import { ButtonComponent } from '@wm-front/shared/buttons'

export const DATE_RANGE_PICKER_FORMATS = {
  display: {
    dateA11yLabel: 'LL',
    dateInput: 'MM/DD/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
    monthYearLabel: 'MMMM YYYY',
  },
}

@UntilDestroy()
@Component({
  selector: 'wm-front-date-range-picker',
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDatepickerModule,
    MatNativeDateModule,
    NgClickOutsideDelayOutsideDirective,
    InputDateComponent,
    FormsModule,
    ButtonComponent,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: DayjsDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_RANGE_PICKER_FORMATS },
  ],
})
export class DateRangePickerComponent implements OnChanges, OnInit {
  /** Emits when value changes */
  @Input() public period: DateFilter

  /** Override the activation of reset button */
  @Input() public activeResetBtn = false

  @Input() public startDate = dayJS()

  /** Minimum date validation */
  @Input() public minDate: dayJS.Dayjs

  /** Maximum date validation */
  @Input() public maxDate: dayJS.Dayjs

  /** Maximum month range */
  @Input() public maxMonthRange: number

  /** Active send date when one date selected */
  @Input() public activeWithOneDate: boolean = false

  /** Emits when value changes */
  @Output() public valueChanged: EventEmitter<DateFilter> = new EventEmitter()

  /** Emits when value reset */
  @Output() public reseted: EventEmitter<void> = new EventEmitter()

  /** Emits when it should close */
  @Output() public shouldClose: EventEmitter<void> = new EventEmitter()

  /** Display max range warning */
  public displayMaxRange = false

  /** The component which will replace the header */
  public headerComponent = DateRangePickerHeaderComponent

  /** Selected range values */
  public selectedRangeValue: DateRange<dayJS.Dayjs> =
    new DateRange<dayJS.Dayjs>(null, null)

  private _i18Service = inject(I18nService)

  private _materialDateAdapter = inject<DateAdapter<dayJS.Dayjs>>(DateAdapter)

  /* eslint-disable */
  @TrackChanges('period', 'handlePeriodChanges')
  public ngOnChanges(changes: SimpleChanges): void {}

  public ngOnInit(): void {
    this.manageLocale()
  }

  /** Reset period filter */
  public resetPeriod(): void {
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(null, null)

    this.reseted.emit()
  }

  public shortcut(duration: 1 | 7 | 30 = 1): void {
    const now = dayJS()
    this.selectedChange(now.subtract(duration, 'day'))
    this.selectedChange(now)
  }

  /** Set the range values depending on event trigger and emit when both start and end are set
   * @param { dayJS.Dayjs }
   */
  public selectedChange(event: dayJS.Dayjs): void {
    this.displayMaxRange = false
    if (
      !this.selectedRangeValue?.start ||
      (this.selectedRangeValue?.start && this.selectedRangeValue?.end)
    ) {
      this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
        event.startOf('d'),
        null
      )
      if (this.activeWithOneDate) {
        this.emitCurrentValue()
      }
    } else {
      if (event.isAfter(this.selectedRangeValue.start)) {
        const start = this.selectedRangeValue.start
        const end = event
        if (end.diff(start, 'month') > this.maxMonthRange) {
          this.displayMaxRange = true
        } else {
          this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
            start.startOf('d'),
            event.endOf('d')
          )
        }
      } else if (event.isSame(this.selectedRangeValue.start)) {
        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
          event.startOf('d'),
          event.endOf('d')
        )
      } else {
        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
          event.startOf('d'),
          null
        )
      }
      this.emitCurrentValue()
    }
  }

  /**
   * When the start date will change
   * @param {dayJS.Dayjs} date
   * @param {MatCalendar} calendar
   */
  public onStartDateInputted(
    date: dayJS.Dayjs,
    calendar: MatCalendar<dayJS.Dayjs>
  ): void {
    const start = date.startOf('d')
    const end = this.selectedRangeValue?.end?.isAfter(start)
      ? this.selectedRangeValue.end
      : null
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(start, end)
    calendar.activeDate = start

    if (end && end.diff(start, 'month') > this.maxMonthRange) {
      this.displayMaxRange = true
    } else {
      this.emitCurrentValue()
    }
  }

  /**
   * When the end date will change
   * @param {dayJS.Dayjs} date
   * @param {MatCalendar} calendar
   */
  public onEndDateInputted(
    date: dayJS.Dayjs,
    calendar: MatCalendar<dayJS.Dayjs>
  ): void {
    const end = date.endOf('d')
    const start = this.selectedRangeValue?.start?.isBefore(end)
      ? this.selectedRangeValue.start
      : null
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
      this.selectedRangeValue?.start?.isBefore(end)
        ? this.selectedRangeValue.start
        : null,
      end
    )
    calendar.activeDate = end

    if (end.diff(start, 'month') > this.maxMonthRange) {
      this.displayMaxRange = true
    } else {
      this.emitCurrentValue()
    }
  }

  /**
   * Let's update the parent with the lasted news
   * @private
   */
  private emitCurrentValue(): void {
    this.valueChanged.emit({
      after: this.selectedRangeValue.start?.toISOString(),
      before: this.selectedRangeValue.end?.toISOString(),
    })
  }

  /** Handle changes in period */
  private handlePeriodChanges(period: DateFilter): void {
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
      period?.after && dayJS(period.after),
      period?.before && dayJS(period.before)
    )
  }

  /**
   * Will manage the locale
   * @private
   */
  private manageLocale(): void {
    dayJS.locale(this._i18Service.languageWithoutLocale)
    this._materialDateAdapter.setLocale(this._i18Service.languageWithoutLocale)
  }
}
